import { UNIT_TYPES } from '@bienenkampf/shared';
import {
  flightTimeMinutes,
  hexDistance,
  minCourt,
  resolveColonize,
  totalOf,
  type UnitCounts,
} from '@bienenkampf/sim';
import { Badge, Button } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatDuration, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import { UnitInputs, draftToCounts, type UnitDraft } from './CombatSimulator.js';

export interface ColonizeTarget {
  hiveId: number;
  q: number;
  r: number;
  name: string;
  /** Punkte des Ziels; 0 bei unbewohntem Stock */
  points: number;
  /** unbewohnt (Kolonie) oder weiselloser fremder Stock (Uebernahme) */
  free: boolean;
}

/**
 * Kolonisationsformular: Reservekoenigin + Hofstaat zu einem unbewohnten oder weisellosen Stock.
 * Zeigt Mindesthofstaat und erwartete Verluste nach der Konzeptformel (Restbevoelkerung und
 * Waechterwabe des Ziels aus dem letzten Spaehbericht).
 */
export function ColonizeForm({
  worldId,
  myHive,
  target,
  remainingPop = 0,
  guardCombLevel = 0,
}: {
  worldId: number;
  myHive: { id: number; q: number; r: number };
  target: ColonizeTarget;
  remainingPop?: number;
  guardCombLevel?: number;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow();
  const [queenId, setQueenId] = useState('');
  const [draft, setDraft] = useState<UnitDraft>({});
  const mine = useQuery(trpc.hive.get.queryOptions({ hiveId: myHive.id }));
  const cfg = useQuery(trpc.world.config.queryOptions({ worldId }));
  const worlds = useQuery(trpc.world.list.queryOptions());
  const world = worlds.data?.find((w) => w.id === worldId);
  const seasonKey = world?.season.season ?? 'TRACHT';
  const send = useMutation(
    trpc.movement.sendColonize.mutationOptions({
      onSuccess: () => {
        setDraft({});
        setQueenId('');
        void qc.invalidateQueries({ queryKey: trpc.movement.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.hive.get.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.auth.me.queryKey() });
      },
    }),
  );

  const available = useMemo(() => {
    const out: Partial<Record<(typeof UNIT_TYPES)[number], number>> = {};
    for (const u of mine.data?.units ?? []) out[u.type] = u.count;
    return out;
  }, [mine.data]);
  const court: UnitCounts = draftToCounts(draft);
  const total = totalOf(court);
  const tooMany = UNIT_TYPES.some((t) => (court[t] ?? 0) > (available[t] ?? 0));
  const reserve = mine.data?.queen.reserve ?? [];
  const targetPoints = target.free ? 0 : target.points;
  const balance = cfg.data?.balance;
  const required = balance ? minCourt(balance, targetPoints) : 0;
  const estimate =
    balance && total > 0
      ? resolveColonize(balance, court, {
          seasonLossMult: Number(cfg.data!.seasons[seasonKey].colonizeLossMult),
          remainingPop: target.free ? 0 : remainingPop,
          guardCombLevel: target.free ? 0 : guardCombLevel,
          targetPoints,
        })
      : null;
  const slowest = UNIT_TYPES.reduce(
    (m, t) => ((court[t] ?? 0) > 0 && balance ? Math.max(m, balance.units[t].minPerHex) : m),
    0,
  );
  const minutes =
    cfg.data && slowest > 0
      ? flightTimeMinutes({
          from: myHive,
          to: target,
          slowestMinPerHex: slowest,
          seasonSpeedMult: Number(cfg.data.seasons[seasonKey].speedMult),
          worldSpeed: cfg.data.speed,
        })
      : 0;

  if (mine.data && reserve.length === 0)
    return (
      <p className="text-xs text-amber-400">
        Dafür brauchst du eine Reservekönigin (Weiselzelle bauen, Weisellarve ansetzen).
      </p>
    );

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!queenId || total === 0 || tooMany) return;
        send.mutate({
          worldId,
          hiveId: myHive.id,
          queenId: Number(queenId),
          targetHiveId: target.hiveId,
          units: court,
        });
      }}
    >
      <p className="text-xs text-stone-400">
        Mindesthofstaat <b className="text-stone-200">{formatNumber(required)}</b> Bienen müssen
        ankommen (800 + 0,35 × Zielpunkte). Verluste: 30 % × Saison × (1 + Restbevölkerung/1000 +
        0,1 × Wächterwabe). Scheitert es, stirbt die Königin.
        {target.free && ' Die Kolonie startet mit Brutwabe 1, leerem Lager und den Überlebenden.'}
      </p>
      <select
        className="rounded-md border border-stone-700 bg-stone-900 px-2 py-1.5 text-sm"
        value={queenId}
        onChange={(e) => setQueenId(e.target.value)}
      >
        <option value="">Reservekönigin wählen…</option>
        {reserve.map((q) => (
          <option key={q.id} value={q.id}>
            ♛ {q.name}
          </option>
        ))}
      </select>
      <UnitInputs value={draft} onChange={setDraft} max={available} />
      {estimate && (
        <div className="rounded-lg bg-stone-900 p-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={estimate.success ? 'green' : 'red'}>
              {estimate.success ? 'reicht' : 'zu wenig'}
            </Badge>
            <span className="text-stone-300 tabular-nums">
              Verlust ≈ {Math.round(estimate.lossRate * 100)} % →{' '}
              {formatNumber(estimate.survivorCount)} von {formatNumber(total)} kommen an (nötig{' '}
              {formatNumber(required)})
            </span>
          </div>
          <div className="mt-1 text-stone-500">
            {hexDistance(myHive, target)} Hex · Ankunft{' '}
            {formatAbsRel(new Date(now.getTime() + minutes * 60_000), now)} (
            {formatDuration(minutes * 60)})
            {seasonKey === 'WINTER' && ' · Winter: dreifache Verluste'}
          </div>
        </div>
      )}
      <Button
        type="submit"
        variant={target.free ? 'primary' : 'danger'}
        disabled={
          !queenId ||
          total === 0 ||
          tooMany ||
          send.isPending ||
          (estimate ? !estimate.success : false)
        }
        loading={send.isPending}
      >
        {target.free ? 'Kolonie gründen' : `${target.name} übernehmen`}
      </Button>
      {send.error && <p className="text-sm text-red-400">{send.error.message}</p>}
      {send.isSuccess && (
        <p className="text-sm text-emerald-400">Unterwegs. Der Bericht kommt nach der Ankunft.</p>
      )}
    </form>
  );
}
